class QuickView extends HTMLElement {
  constructor() {
    super();

    this._mounted = false;
    this._open = false;
    this._busy = false;

    this._activeTrigger = null;
    this._lastFocus = null;

    this._openToken = 0;
    this._abort = null;

    this._onDocClick = this._onDocClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onCartAdded = this._onCartAdded.bind(this);
  }

  connectedCallback() {
    if (this._mounted) return;
    this._mounted = true;

    this.innerHTML = `
      <div class="qv-backdrop" data-qv-backdrop></div>

      <div class="qv-panel" role="dialog" aria-modal="true" aria-hidden="true" data-qv-panel>
        <div class="qv-dialog" tabindex="-1" data-qv-dialog>
          <button class="qv-close" type="button" aria-label="Close" data-qv-close>
            <span class="icon-close icon-close-popup"></span>
          </button>

          <div class="qv-body" data-qv-content>
            <div class="qv-error" data-qv-error hidden style="color:#b00020;margin-top:10px;"></div>
          </div>
        </div>
      </div>
    `;

    this.backdrop = this.querySelector('[data-qv-backdrop]');
    this.panel = this.querySelector('[data-qv-panel]');
    this.dialog = this.querySelector('[data-qv-dialog]');
    this.contentEl = this.querySelector('[data-qv-content]');
    this.errorEl = this.querySelector('[data-qv-error]');

    this.backdrop.addEventListener('click', () => this.close());
    this.querySelectorAll('[data-qv-close]').forEach((b) => b.addEventListener('click', () => this.close()));

    document.addEventListener('click', this._onDocClick, true);
    document.addEventListener('keydown', this._onKeyDown);

    document.addEventListener('cart:added', this._onCartAdded);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick, true);
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('cart:added', this._onCartAdded);
  }

  _setTriggerLoading(trigger, on) {
    if (!trigger) return;
    trigger.classList.toggle("is-loading", !!on);
    trigger.setAttribute("aria-busy", on ? "true" : "false");
  }

  _clearTriggerLoading() {
    if (this._activeTrigger) this._setTriggerLoading(this._activeTrigger, false);
  }

  /* ===================== EVENTS ===================== */

  _onCartAdded(e) {
    if (!this._open) return;
    if (!this.contains(e.target)) return;
    this.close();
  }

  _onDocClick(e) {
    if (this._open) {
      const isTrigger = e.target.closest('a.quickview');
      if (!isTrigger) return;
    }

    const trigger = e.target.closest('a.quickview');
    if (!trigger) return;

    e.preventDefault();

    if (this._busy) return;

    let url = trigger.getAttribute('data-product-url');
    if (!url) return;

    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set('view', 'quickview');
      url = u.toString();
    } catch (_) {
      url += (url.includes('?') ? '&' : '?') + 'view=quickview';
    }

    const title = trigger.getAttribute('data-title') || 'Quick view';
    this._setTriggerLoading(trigger, true);
    this.open({ url, title, trigger });
  }

  _onKeyDown(e) {
    if (!this._open) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }

    if (e.key === 'Tab') this._trapFocus(e);
  }

  /* ===================== OPEN/CLOSE ===================== */

  async open({ url, title, trigger }) {
    this._busy = true;
    const token = ++this._openToken;

    try { this._abort?.abort(); } catch (_) {}
    this._abort = new AbortController();

    this._activeTrigger = trigger;
    this._setTriggerLoading(trigger, true);
    this._lastFocus = document.activeElement;

    if (this._open) this._setOpen(false);

    this._setError('');
    this._clearContentKeepError();

    try {
      const html = await this._fetchHtml(url, this._abort.signal);
      if (token !== this._openToken) return;

      this._setContentHTML(html);

      await this._ensureScriptsLoadedFromContent(this.contentEl);
      if (token !== this._openToken) return;

      await this._afterRender();
      if (token !== this._openToken) return;

      this._wireInternalClose();

      this._setOpen(true);

      requestAnimationFrame(() => {
        this.dialog.focus({ preventScroll: true });
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;

      this._setError(err?.message || 'Failed to load quick view.');
      this._setOpen(true);
    } finally {
      this._busy = false;
      this._clearTriggerLoading();
    }
  }

  close() {
    if (!this._open) return;

    try { this._abort?.abort(); } catch (_) {}

    this._setOpen(false);
    this._setError('');
    this._clearContentKeepError();

    this._clearTriggerLoading();

    const backTo = this._activeTrigger || this._lastFocus;
    if (backTo && backTo.focus) backTo.focus({ preventScroll: true });

    this._activeTrigger = null;
    this._lastFocus = null;
  }

  _setOpen(on) {
    this._open = on;

    this.backdrop.classList.toggle('is-open', on);
    this.panel.classList.toggle('is-open', on);
    this.panel.setAttribute('aria-hidden', on ? 'false' : 'true');

    document.documentElement.classList.toggle('qv-lock', on);
    document.body.classList.toggle('qv-lock', on);
  }

  _setError(msg) {
    if (!msg) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      return;
    }
    this.errorEl.hidden = false;
    this.errorEl.textContent = msg;
  }

  _clearContentKeepError() {
    const errNode = this.errorEl;
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(errNode);
  }

  _setContentHTML(html) {
    const errNode = this.errorEl;
    this.contentEl.innerHTML = html;
    this.contentEl.appendChild(errNode);
  }

  /* ===================== FETCH ===================== */

  async _fetchHtml(url, signal) {
    const res = await fetch(url, {
      signal,
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) throw new Error(`Request failed (${res.status})`);
    return await res.text();
  }

  /* ===================== SCRIPT LOADER ===================== */

  async _ensureScriptsLoadedFromContent(root) {
    const scripts = Array.from(root.querySelectorAll('script[src]'));
    if (!scripts.length) return;

    for (const s of scripts) {
      const src = s.getAttribute('src');
      if (!src) continue;

      await this._loadScriptOnce(src);

      try { s.remove(); } catch (_) {}
    }
  }

  _loadScriptOnce(src) {
    return new Promise((resolve) => {
      const exists = Array.from(document.scripts).some((x) => (x.src || '') === src);
      if (exists) return resolve();

      const el = document.createElement('script');
      el.src = src;
      el.async = false;
      el.onload = () => resolve();
      el.onerror = () => resolve();
      document.head.appendChild(el);
    });
  }

  /* ===================== INIT AFTER INJECT ===================== */

  async _afterRender() {
    try {
      if (window.theme?.init) window.theme.init();
      if (window.Theme?.init) window.Theme.init();
    } catch (_) {}

    const tags = ['quantity-input-selector', 'tf-variant-picker', 'tf-buy-actions']
      .filter((tag) => this.contentEl.querySelector(tag));

    await this._waitCustomElements(tags, 600);

    if (customElements.upgrade) {
      try { customElements.upgrade(this.contentEl); } catch (_) {}
    }

    this.contentEl.querySelectorAll('*').forEach((el) => {
      try { if (typeof el.init === 'function') el.init(); } catch (_) {}
    });
  }

  _waitCustomElements(tags, timeoutMs = 600) {
    if (!tags || !tags.length) return Promise.resolve();
    const waits = tags.map((tag) => this._whenDefinedWithTimeout(tag, timeoutMs));
    return Promise.allSettled(waits).then(() => undefined);
  }

  _whenDefinedWithTimeout(tag, timeoutMs) {
    if (customElements.get(tag)) return Promise.resolve();

    return Promise.race([
      customElements.whenDefined(tag),
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }

  /* ===================== INTERNAL CLOSE ===================== */

  _wireInternalClose() {
    this.contentEl.querySelectorAll('[data-qv-close]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });
  }

  /* ===================== FOCUS TRAP ===================== */

  _trapFocus(e) {
    const focusables = this.querySelectorAll(
      '.qv-panel.is-open a[href], .qv-panel.is-open button:not([disabled]), .qv-panel.is-open input, .qv-panel.is-open select, .qv-panel.is-open textarea, .qv-panel.is-open [tabindex]:not([tabindex="-1"])'
    );

    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('quick-view')) {
  customElements.define('quick-view', QuickView);
}
